import { useEffect, useState } from 'react';
import { inventoryAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import VehicleAccessories from '../components/VehicleAccessories';
import { PageHeader, Button, Table, Modal, Input, Select, Loading, Alert, Badge } from '../components/ui';

const CATEGORIES = ['part','tool','accessory','consumable','other'];
const UNITS = ['pcs','L','kg','set','pr','btl','box','m','kit'];

const empty = {
  name:'', category:'part', sku:'', quantity:'', min_quantity:'5',
  unit:'pcs', unit_price:'', location:'', notes:'',
};

export default function Inventory() {
  const { can } = useAuth();
  const [tab, setTab] = useState('vehicle');
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [search,  setSearch]  = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState(empty);
  const [saving,  setSaving]  = useState(false);

  const flash = (msg, isErr=false) => {
    isErr ? setError(msg) : setSuccess(msg);
    setTimeout(() => isErr ? setError('') : setSuccess(''), 3500);
  };

  const load = () => {
    setLoading(true);
    inventoryAPI.getAll({ search, category: filterCat, limit: 100 })
      .then(r => setItems(r.data.data))
      .catch(() => flash('Failed to load inventory', true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [search, filterCat]);

  const openAdd  = () => { setEditing(null); setForm(empty); setModal(true); };
  const openEdit = i  => { setEditing(i); setForm({...empty,...i}); setModal(true); };

  const save = async e => {
    e.preventDefault(); setSaving(true);
    try {
      editing ? await inventoryAPI.update(editing.id, form) : await inventoryAPI.create(form);
      setModal(false); load();
      flash(editing ? 'Item updated' : 'Item added');
    } catch (err) { flash(err.response?.data?.message || 'Save failed', true); }
    finally { setSaving(false); }
  };

  const remove = async id => {
    if (!window.confirm('Delete this item?')) return;
    try { await inventoryAPI.remove(id); load(); flash('Item deleted'); }
    catch { flash('Delete failed', true); }
  };

  const stockStatus = item => {
    if (+item.quantity === 0)                return { label:'Out of Stock', cls:'bg-red-100 text-red-700' };
    if (+item.quantity <= +item.min_quantity) return { label:'Low Stock',    cls:'bg-amber-100 text-amber-700' };
    return { label:'In Stock', cls:'bg-green-100 text-green-700' };
  };

  const catCounts = CATEGORIES.reduce((a, c) => ({ ...a, [c]: items.filter(i => i.category === c).length }), {});
  const lowStock  = items.filter(i => +i.quantity <= +i.min_quantity).length;

  return (
    <div>
      <PageHeader
        title="Inventory"
        sub={tab === 'vehicle' ? 'Tools & accessories held by each vehicle' : `${items.length} items · ${lowStock > 0 ? `⚠ ${lowStock} low stock` : 'all stocked'}`}
        action={tab === 'stock' && can('admin','transport_officer') && <Button onClick={openAdd}>+ Add Item</Button>}
      />

      <div className="flex gap-2 mb-4">
        {[['vehicle','🚗 Vehicle Items'],['stock','📦 Store Stock']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${tab === k ? 'bg-primary-600 text-white border-primary-600' : 'bg-white'}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'vehicle' ? <VehicleAccessories /> : (<>

      {error   && <div className="mb-4"><Alert type="error"   message={error}   /></div>}
      {success && <div className="mb-4"><Alert type="success" message={success} /></div>}

      {/* Category filter pills */}
      <div className="flex gap-2 flex-wrap mb-5">
        <button onClick={() => setFilterCat('')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all
            ${!filterCat ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'}`}>
          All ({items.length})
        </button>
        {CATEGORIES.map(c => (
          <button key={c} onClick={() => setFilterCat(c === filterCat ? '' : c)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border capitalize transition-all
              ${filterCat === c ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'}`}>
            {c} ({catCounts[c] || 0})
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mb-5">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or SKU…"
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg w-64 outline-none focus:ring-2 focus:ring-primary-300" />
      </div>

      {loading ? <Loading /> : (
        <Table headers={['Name','SKU','Category','Quantity','Min Stock','Unit Price','Location','Status','Actions']}>
          {items.map(item => {
            const s = stockStatus(item);
            return (
              <tr key={item.id} className={`hover:bg-gray-50 ${+item.quantity === 0 ? 'bg-red-50' : ''}`}>
                <td className="px-4 py-3">
                  <p className="font-medium text-sm text-gray-800">{item.name}</p>
                  {item.notes && <p className="text-xs text-gray-400 truncate max-w-[160px]">{item.notes}</p>}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-500">{item.sku || '—'}</td>
                <td className="px-4 py-3 capitalize text-sm">{item.category}</td>
                <td className="px-4 py-3">
                  <span className={`text-sm font-bold ${+item.quantity <= +item.min_quantity ? 'text-amber-600' : 'text-gray-800'}`}>
                    {item.quantity}
                  </span>
                  <span className="text-xs text-gray-400 ml-1">{item.unit}</span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">{item.min_quantity} {item.unit}</td>
                <td className="px-4 py-3 text-sm">
                  {item.unit_price ? `Rs. ${Number(item.unit_price).toLocaleString('en-LK')}` : '—'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">{item.location || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    {can('admin','transport_officer') && (
                      <Button variant="ghost" size="sm" onClick={() => openEdit(item)}>Edit</Button>
                    )}
                    {can('admin') && (
                      <Button variant="danger" size="sm" onClick={() => remove(item.id)}>Del</Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {items.length === 0 && (
            <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">No inventory items yet</td></tr>
          )}
        </Table>
      )}

      </>)}

      <Modal open={modal} onClose={() => setModal(false)}
        title={editing ? `Edit — ${editing.name}` : 'Add Inventory Item'} size="md">
        <form onSubmit={save} className="space-y-4">
          <Input label="Item Name *" value={form.name}
            onChange={e => setForm({...form, name: e.target.value})} required
            placeholder="e.g. Engine Oil 5W-30" />
          <div className="grid grid-cols-2 gap-4">
            <Select label="Category" value={form.category}
              onChange={e => setForm({...form, category: e.target.value})}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
            <Input label="SKU / Code" value={form.sku}
              onChange={e => setForm({...form, sku: e.target.value})}
              placeholder="e.g. OIL-5W30-1L" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Input label="Quantity *" type="number" value={form.quantity}
              onChange={e => setForm({...form, quantity: e.target.value})} required />
            <Input label="Min. Stock" type="number" value={form.min_quantity}
              onChange={e => setForm({...form, min_quantity: e.target.value})} />
            <Select label="Unit" value={form.unit}
              onChange={e => setForm({...form, unit: e.target.value})}>
              {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Unit Price (Rs.)" type="number" step="0.01" value={form.unit_price}
              onChange={e => setForm({...form, unit_price: e.target.value})}
              placeholder="e.g. 850.00" />
            <Input label="Storage Location" value={form.location}
              onChange={e => setForm({...form, location: e.target.value})}
              placeholder="e.g. Shelf A-1" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none
                         focus:ring-2 focus:ring-primary-300 h-16 resize-none" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
